import dayjs from "dayjs";
import { clubDate } from "./clubDates";
import { formatCurrency } from "./currency";

// Self-serve Sotravel Club events are ordinary projects/trips whose
// `club_user_id` is set (the API also attaches a `club` summary). The public
// event page and checkout use these helpers to swap trip-only copy
// ("1D0N", "Designed by Sotravel", flights, group leaders) for event copy.

// Clubs also host overseas trips (projects.is_club_trip, Oct 2026). Those render
// as normal Sotravel trip pages with a "Hosted by" card, so the event-only
// helpers below exclude them; isClubHosted covers both (no credits, no group
// leader, the club is the host).
export const isClubHosted = (row) => Boolean(row?.club_user_id || row?.club?.id);
export const isClubTrip = (row) => isClubHosted(row) && row?.is_club_trip === true;
export const isClubEvent = (row) => isClubHosted(row) && row?.is_club_trip !== true;

// Club trips: 40% deposit, balance charged 45 days before, nothing refundable.
export const CLUB_TRIP_DEPOSIT_PERCENT = 40;
export const CLUB_TRIP_BALANCE_DAYS_BEFORE = 45;
// Free-reserve tooltip / checkout copy: club trips have no early-bird price, the
// reservation just holds a spot until the host confirms.
export const freeReserveHoverCopy = (t, row, amount) =>
  isClubTrip(row)
    ? t("club_trip_free_reserve_hover", {
        amount,
        defaultValue: "Reserve your spot for free. {{amount}} (the deposit) is charged automatically when the host confirms the trip.",
      })
    : t("free_reserve_hover", { amount });

// Club trips: seats removed from a booking. Mirrors sotravel_be service/club/cancellation.js
// clubTripSeatRemoval: paid money first covers the deposit of the seats that stay, only the
// rest is kept for the removed seats (at most their deposit); unpaid parts aren't charged.
export const clubTripSeatRemoval = ({
  removedValue,
  depositPercent,
  paid = 0,
  alreadyKept = 0,
  firstDue = 0,
  secondDue = 0,
  reserved = false,
  fullyPaid = false,
}) => {
  const v = Math.max(0, Number(removedValue) || 0);
  const pct = (Number(depositPercent) || 0) / 100;
  const first = Math.max(0, Number(firstDue) || 0);
  const second = Math.max(0, Number(secondDue) || 0);
  if (!(v > 0) || fullyPaid) return { first: 0, second: 0, kept: 0 };
  if (reserved) {
    const total = Math.max(0, first + second - v);
    let newSecond = second > 0 ? Math.max(0, total - Math.min(first, Math.ceil(total * pct))) : 0;
    newSecond = Math.min(second, newSecond);
    return { first: first - (total - newSecond), second: second - newSecond, kept: 0 };
  }
  const paidForSeats = Math.max(0, (Number(paid) || 0) - (Number(alreadyKept) || 0));
  const remainingValue = Math.max(0, paidForSeats + second - v);
  const excess = Math.max(0, paidForSeats - Math.ceil(remainingValue * pct));
  const kept = Math.min(excess, Math.ceil(v * pct));
  const newSecond = Math.min(second, Math.max(0, remainingValue - (paidForSeats - kept)));
  return { first: 0, second: second - newSecond, kept };
};

export const clubTripCancellationPolicy = (t, trip) => {
  const args = {
    deposit: Number(trip?.deposit_percentage) || CLUB_TRIP_DEPOSIT_PERCENT,
    days:
      trip?.days_before_all_nonrefundable != null
        ? Number(trip.days_before_all_nonrefundable) + 3
        : CLUB_TRIP_BALANCE_DAYS_BEFORE,
  };
  // A confirmed trip takes the deposit upfront, so there's no free reservation to mention.
  if (trip?.sotravel_fulfilment_status === "confirmed") {
    return t("club_trip_cancel_policy_confirmed", {
      ...args,
      defaultValue:
        "This trip is confirmed. The {{deposit}}% deposit you pay now is non-refundable, and the balance is charged {{days}} days before the trip and is non-refundable too. If the host cancels the trip, you get a full refund.",
    });
  }
  return t("club_trip_cancel_policy", {
    ...args,
    defaultValue:
      "Free to cancel until the trip is confirmed. Once it's confirmed, the {{deposit}}% deposit is charged and is non-refundable, and the balance is charged {{days}} days before the trip and is non-refundable too. If the host cancels the trip, you get a full refund.",
  });
};

export const clubEventDuration = (row, t) => {
  const minutes = Number(row?.creation_slot_duration_minutes);
  if (Number.isFinite(minutes) && minutes > 0) {
    if (minutes < 60) return t("club_duration_minutes", { count: minutes, defaultValue: "{{count}} min" });
    if (minutes % 60 === 0) return t("club_duration_hours", { count: minutes / 60, defaultValue: "{{count}}h" });
    return t("club_duration_hours_minutes", {
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60,
      defaultValue: "{{hours}}h {{minutes}}m",
    });
  }
  return t("club_event_label", { defaultValue: "Local event" });
};

// Duration label for the trip page / checkout header: club events show their
// slot length, weekly Soclub programmes their week count, trips "xDyN".
export const tripDurationLabel = (row, t) => {
  if (isClubEvent(row)) return clubEventDuration(row, t);
  if (row?.soclub_weekly_type) {
    return `${row.num_nights} ${Number(row.num_nights) === 1 ? "week" : "weeks"}`;
  }
  return t("trip_details_duration", { days: Number(row?.num_nights) + 1, nights: row?.num_nights });
};

// Guest cancellation rule of a club event (`trip.club_cancellation_policy` from the
// API): the host's cancellation deadline — 95% refund by then (5% admin fee),
// no refund after — or "No cancellations". Mirrors the club branch of sotravel_be
// utils/cancellationPolicy.js (the text frozen onto the booking).
export const CLUB_DEFAULT_FREE_CANCEL_DAYS = 14;
export const CLUB_LATE_CANCEL_FEE_PERCENT = 5;

export const clubPolicyOf = (policy) => {
  const days = Number(policy?.free_cancel_days);
  const fee = Number(policy?.late_fee_percent);
  return {
    allowed: policy?.allowed !== false,
    freeCancelDays:
      policy?.free_cancel_days != null && Number.isInteger(days) && days >= 0
        ? days
        : CLUB_DEFAULT_FREE_CANCEL_DAYS,
    feePercent: Number.isFinite(fee) && fee >= 0 ? fee : CLUB_LATE_CANCEL_FEE_PERCENT,
  };
};

export const clubCancellationPolicy = (t, { isFree, policy, startDate }) => {
  const { allowed, freeCancelDays, feePercent } = clubPolicyOf(policy);
  if (!allowed) {
    return t("club_cancel_policy_none", {
      defaultValue: "No cancellations: you can still cancel to free up your spot, but there's no refund.",
    });
  }
  if (freeCancelDays === 0) {
    return t("club_cancel_policy_anytime", {
      defaultValue: "Cancellation policy: cancel before the event starts.",
    });
  }
  const event = startDate ? dayjs(String(startDate).slice(0, 10)) : null;
  const deadline = event?.isValid() ? event.subtract(freeCancelDays, "day") : null;
  if (!deadline || dayjs().startOf("day").isAfter(deadline, "day")) {
    return t("club_cancel_policy_late", { defaultValue: "Cancellation policy: no refund after the cancellation deadline." });
  }
  if (isFree) {
    return t("club_cancel_policy_free", {
      date: clubDate(deadline.format("YYYY-MM-DD"), "dayMonthYear"),
      defaultValue: "Cancel by {{date}}. After that, cancellation is not available.",
    });
  }
  return t("club_cancel_policy_paid", {
    fee: feePercent,
    date: clubDate(deadline.format("YYYY-MM-DD"), "dayMonthYear"),
    defaultValue: "Cancel by {{date}} for a refund minus a {{fee}}% administration fee. After that, there is no refund.",
  });
};

// Short checkout copy; the full rule is available from the Learn more modal.
export const clubCancellationSummary = (t, { isFree, policy, startDate }) => {
  const { allowed, freeCancelDays } = clubPolicyOf(policy);
  if (!allowed) return t("club_cancel_policy_none_short", { defaultValue: "No refunds available" });
  if (freeCancelDays === 0) {
    return t("club_cancel_summary_before_start", { defaultValue: "Cancel before the event starts" });
  }
  const event = startDate ? dayjs(String(startDate).slice(0, 10)) : null;
  const deadline = event?.isValid() ? event.subtract(freeCancelDays, "day") : null;
  if (!deadline) return t("club_cancel_summary_policy", { defaultValue: "Cancellation policy" });
  return isFree
    ? t("club_cancel_summary_free", {
        date: clubDate(deadline.format("YYYY-MM-DD"), "dayMonthYear"),
        defaultValue: "Free cancellation until {{date}}",
      })
    : t("club_cancel_summary_paid", {
        date: clubDate(deadline.format("YYYY-MM-DD"), "dayMonthYear"),
        defaultValue: "Refund available until {{date}}",
      });
};

// What a guest gets back for `amount` at refund share `share` (0.95 or 0) —
// display only; the BE computes the real refund (service/club/cancellation.js).
const ZERO_DECIMAL = new Set(["jpy", "krw", "vnd"]); // as sotravel_be constants/currencies.js
export const clubShareOf = (amount, share, currency) => {
  const value = Number(amount) || 0;
  if (share >= 1) return value;
  if (!(share > 0)) return 0;
  return ZERO_DECIMAL.has(String(currency || "").toLowerCase())
    ? Math.round(value * share)
    : Math.round(value * share * 100) / 100;
};

// Money for club refunds: the administration-fee refund can carry cents
// (SGD 23.75) — formatCurrency rounds to whole units, so it would say SGD 24.
export const clubMoney = (amount, currency) => {
  const n = Number(amount) || 0;
  const cents = Math.round(Math.abs(n) * 100) % 100;
  if (cents === 0) return formatCurrency(Math.round(n), currency);
  const code = String(currency || "sgd").toUpperCase();
  const sign = n < 0 ? "- " : "";
  const abs = Math.abs(n).toFixed(2);
  if (code === "TWD") return `${sign}NT$${abs}`;
  if (code === "USD") return `${sign}USD ${abs}`;
  try {
    const language = localStorage.getItem("locale") || "en";
    return `${sign}${new Intl.NumberFormat(language, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(n))}`;
  } catch (e) {
    return `${sign}${code} ${abs}`;
  }
};
